import { Cookie } from "lucide-react";
import { useState } from "react";
import { useLanguage } from "../../i18n/language-provider";
import { Button } from "../ui/button";

/**
 * First-visit disclosure of what Optivo stores in the browser.
 *
 * ## Why this is a notice and not a consent banner
 *
 * Everything Optivo writes to the browser is either strictly necessary (the
 * Logto session cookie — without it there is no login) or explicitly
 * user-requested (theme, skin, language, the drafts cell metric). Both are
 * exempt from the ePrivacy consent requirement, so there is nothing here a
 * visitor could meaningfully refuse. A banner offering "Reject all" that
 * silently keeps the session cookie anyway is worse than no banner: it asks
 * for a decision it cannot honour. What GDPR Art. 13 does require is that the
 * visitor be *told*, which is what this does — once, dismissibly, without
 * blocking the page.
 *
 * This stays accurate only while nothing optional is loaded. Adding analytics,
 * advertising, or Sentry Session Replay (removed from `main.tsx` for exactly
 * this reason) would make consent genuinely necessary, and this component
 * would have to become a real gate with a granular opt-in.
 */

/** Acknowledgement is itself user-requested storage, so it needs no consent. */
export const COOKIE_NOTICE_ACK_KEY = "optivo-cookie-notice-ack";

/**
 * Safari in private mode throws on `localStorage` access. This component
 * mounts above the entire app, so an uncaught throw would white-screen every
 * page — including the public legal documents the payment gateway reads.
 */
function readAck(): boolean {
    try {
        return localStorage.getItem(COOKIE_NOTICE_ACK_KEY) !== null;
    } catch {
        // Unreadable storage means an unacknowledgeable notice. Staying quiet
        // beats showing a banner that can never be dismissed.
        return true;
    }
}

function writeAck(): void {
    try {
        localStorage.setItem(COOKIE_NOTICE_ACK_KEY, new Date().toISOString());
    } catch {
        // Dismissal still applies for this page view.
    }
}

interface CookieNoticeProps {
    /** Keeps the privacy link inside the SPA, as the legal pages do. */
    onNavigate: (path: string) => void;
}

export function CookieNotice({ onNavigate }: CookieNoticeProps) {
    const { t } = useLanguage();
    const [acknowledged, setAcknowledged] = useState(readAck);

    if (acknowledged) return null;

    const handleDismiss = () => {
        writeAck();
        setAcknowledged(true);
    };

    return (
        <div
            role="region"
            aria-label={t.cookieNotice.regionLabel}
            className="fixed inset-x-0 bottom-0 z-50 p-2 sm:p-6 motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-2 motion-safe:duration-200"
        >
            <div className="mx-auto flex max-w-3xl flex-col gap-2 rounded-lg border border-border bg-card p-4 shadow-lg sm:flex-row sm:items-center sm:gap-4 sm:p-5">
                {/* Below sm the stack puts this on a row of its own, so a
                    decorative 20px glyph costs ~32px of a band that is already
                    covering the footer identification and the card marks — the
                    two things the gateway reviewer opens the site to check. It
                    is `aria-hidden`, so dropping it below sm costs nothing;
                    `sm:block` restores the desktop row untouched. */}
                <Cookie className="hidden size-5 shrink-0 text-primary sm:block" aria-hidden="true" />
                <div className="flex-1 text-sm leading-relaxed">
                    <p className="font-medium">{t.cookieNotice.title}</p>
                    <p className="mt-1 text-muted-foreground">
                        {t.cookieNotice.body}{" "}
                        <a
                            href="/privacy"
                            onClick={(event) => {
                                event.preventDefault();
                                onNavigate("/privacy");
                            }}
                            className="underline underline-offset-2 transition-colors hover:text-foreground"
                        >
                            {t.cookieNotice.privacyLink}
                        </a>
                    </p>
                </div>
                <Button size="sm" onClick={handleDismiss} className="shrink-0 sm:self-center">
                    {t.cookieNotice.acknowledge}
                </Button>
            </div>
        </div>
    );
}
